import { inject, Injectable } from '@angular/core';
import { HttpClient, HttpParams } from '@angular/common/http';
import { API_BASE_URL, buildApiUrl } from '@core/http/api.config';
import { Observable } from 'rxjs';
import { CreateTemplateRequest } from '@domains/template/model/api/create-template-request.interface';
import { PagePagination } from '@shared/model/page-pagination.interface';
import { TemplateOrderField } from '@domains/template/model/api/template-order-field.enum';
import { ApiOrderDirection } from '@core/models/api-order-direction.enum';
import { UpdateTemplatesRequest } from '@domains/template/model/api/update-template-request.interface';
import { Template } from '@domains/template/model/api/template.type';
import { TemplateListQuery } from '@domains/template/model/api/template-list-query.interface';

@Injectable({
  providedIn: 'root',
})
export class TemplateApi {
  readonly #httpClient = inject(HttpClient);
  readonly #baseUrl = inject(API_BASE_URL);

  create(payload: CreateTemplateRequest[]): Observable<Template> {
    return this.#httpClient.post<Template>(buildApiUrl(this.#baseUrl, '/Templates'), payload);
  }

  getAll({
    categories,
    search = '',
    kinds = [],
    pageNumber = 1,
    itemsPerPage = 999,
    orderField = TemplateOrderField.CREATION_DATE,
    orderDirection = ApiOrderDirection.DESC,
    showOwned,
    showPublic,
  }: TemplateListQuery = {}): Observable<PagePagination<Template>> {
    let params = new HttpParams()
      .set('PageNumber', pageNumber)
      .set('ItemsPerPage', itemsPerPage)
      .set('OrderField', orderField)
      .set('OrderDirection', orderDirection);

    if (categories?.length) {
      for (const category of categories) {
        params = params.append('Categories', String(category));
      }
    }

    if (kinds?.length) {
      for (const kind of kinds) {
        params = params.append('Kinds', String(kind));
      }
    }

    if (search?.length) {
      params = params.append('FullText', search);
    }

    if (showOwned !== undefined) {
      params = params.append('ShowOwned', String(showOwned));
    }

    if (showPublic !== undefined) {
      params = params.append('ShowPublic', String(showPublic));
    }

    return this.#httpClient.get<PagePagination<Template>>(buildApiUrl(this.#baseUrl, `/Templates/list`), {
      params,
    });
  }

  update(payload: UpdateTemplatesRequest[]): Observable<UpdateTemplatesRequest[]> {
    return this.#httpClient.post<UpdateTemplatesRequest[]>(buildApiUrl(this.#baseUrl, `/Templates/admin`), payload);
  }

  templateById<T extends Template>(id: number): Observable<T> {
    return this.#httpClient.get<T>(buildApiUrl(this.#baseUrl, `/Templates/${id}`));
  }

  deleteById(id: string): Observable<boolean> {
    return this.#httpClient.delete<boolean>(buildApiUrl(this.#baseUrl, `/Templates/${id}`));
  }
}
